import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  FileText, Download, Calendar, Filter, FileSpreadsheet,
  Users, DollarSign, AlertCircle, RefreshCw, Settings
} from 'lucide-react';
import { reportesApi } from '../../utils/api';
import clsx from 'clsx';
import toast from 'react-hot-toast';

const MESES = [
  { value: 1, label: 'Enero' },
  { value: 2, label: 'Febrero' },
  { value: 3, label: 'Marzo' },
  { value: 4, label: 'Abril' },
  { value: 5, label: 'Mayo' },
  { value: 6, label: 'Junio' },
  { value: 7, label: 'Julio' },
  { value: 8, label: 'Agosto' },
  { value: 9, label: 'Septiembre' },
  { value: 10, label: 'Octubre' },
  { value: 11, label: 'Noviembre' },
  { value: 12, label: 'Diciembre' }
];

export default function ReportesEspeciales({ tipoImpuesto = '07', titulo = 'Impuesto 911' }) {
  const currentDate = new Date();
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  // Filtros
  const [mes, setMes] = useState(currentDate.getMonth() + 1);
  const [anio, setAnio] = useState(currentDate.getFullYear());

  // Vista activa (resumen, por cliente, detalle)
  const [vistaActiva, setVistaActiva] = useState('resumen');

  // Column config for detalle view
  const [showColConfig, setShowColConfig] = useState(false);
  const ALL_DETAIL_COLS = [
    { id: 'fecha', label: 'Fecha', default: true },
    { id: 'consecutivo', label: 'Consecutivo', default: true },
    { id: 'cliente', label: 'Cliente', default: true },
    { id: 'cedula', label: 'Cédula', default: false },
    { id: 'producto', label: 'Producto', default: true },
    { id: 'moneda', label: 'Moneda', default: false },
    { id: 'subtotalOrig', label: 'Subtotal Original', default: false },
    { id: 'tc', label: 'T/C', default: false },
    { id: 'baseCRC', label: 'Base CRC', default: true },
    { id: 'impuestoCRC', label: 'Impuesto CRC', default: true },
    { id: 'totalFactCRC', label: 'Total Factura CRC', default: true },
    { id: 'tarifa', label: 'Tarifa', default: false },
  ];
  const [detailCols, setDetailCols] = useState(() => {
    try {
      const saved = localStorage.getItem('reporteImpEsp_detailCols');
      if (saved) return JSON.parse(saved);
    } catch (e) {}
    return ALL_DETAIL_COLS.filter(c => c.default).map(c => c.id);
  });
  const toggleDetailCol = (colId) => {
    setDetailCols(prev => {
      const selected = new Set(prev.includes(colId) ? prev.filter(c => c !== colId) : [...prev, colId]);
      const next = ALL_DETAIL_COLS.filter(c => selected.has(c.id)).map(c => c.id);
      localStorage.setItem('reporteImpEsp_detailCols', JSON.stringify(next));
      return next;
    });
  };

  // Configuración según tipo de impuesto
  const config = tipoImpuesto === '07'
    ? { color: 'red', nombre: '911', entidad: 'Sistema de Emergencias 911' }
    : { color: 'rose', nombre: 'Cruz Roja', entidad: 'Cruz Roja Costarricense' };

  // Generar años disponibles (últimos 5 años)
  const aniosDisponibles = [];
  for (let i = 0; i < 5; i++) {
    aniosDisponibles.push(currentDate.getFullYear() - i);
  }

  const cargarReporte = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await reportesApi.getImpuestosEspeciales({
        tipo: tipoImpuesto,
        mes,
        anio
      });
      setData(response.data.data);
    } catch (err) {
      console.error('Error cargando reporte:', err);
      setError(err.response?.data?.error || 'Error al cargar el reporte');
      toast.error('Error al cargar el reporte');
    } finally {
      setLoading(false);
    }
  };

  const exportarExcel = async () => {
    setExporting(true);
    try {
      const response = await reportesApi.exportImpuestosEspeciales({
        tipo: tipoImpuesto,
        mes,
        anio,
        vista: vistaActiva === 'detalle' ? 'factura' : 'cliente'
      });

      // Crear blob y descargar
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Reporte_${config.nombre.replace(/ /g, '_')}_${MESES[mes-1].label}_${anio}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Reporte exportado exitosamente');
    } catch (err) {
      console.error('Error exportando:', err);
      toast.error('Error al exportar el reporte');
    } finally {
      setExporting(false);
    }
  };

  const exportarPdf = async () => {
    setExportingPdf(true);
    try {
      const response = await reportesApi.exportImpuestosEspecialesPdf({
        tipo: tipoImpuesto,
        mes,
        anio,
        vista: vistaActiva === 'detalle' ? 'factura' : 'cliente'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Reporte_${config.nombre.replace(/ /g, '_')}_${MESES[mes-1].label}_${anio}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('PDF exportado exitosamente');
    } catch (err) {
      console.error('Error exportando PDF:', err);
      toast.error('Error al exportar el PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  useEffect(() => {
    cargarReporte();
  }, [tipoImpuesto]);

  const formatCurrency = (amount, currency = 'CRC') => {
    const currencyCode = currency === 'USD' ? 'USD' : 'CRC';
    return new Intl.NumberFormat('es-CR', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatColones = (amount) => {
    return new Intl.NumberFormat('es-CR', {
      style: 'currency',
      currency: 'CRC',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-CR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Obtener total del impuesto específico
  const getTotal = () => {
    if (!data?.resumen) return 0;
    return tipoImpuesto === '07'
      ? data.resumen.impuesto911?.monto || 0
      : data.resumen.cruzRoja?.monto || 0;
  };

  const getTotalDocumentos = () => {
    if (!data?.resumen) return 0;
    return tipoImpuesto === '07'
      ? data.resumen.impuesto911?.documentos || 0
      : data.resumen.cruzRoja?.documentos || 0;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            {titulo}
          </h1>
          <p className="text-gray-500 mt-1">
            Reporte para {config.entidad}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={exportarPdf}
            disabled={exportingPdf || !data || getTotalDocumentos() === 0}
            className={clsx(
              'flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors',
              exportingPdf || !data || getTotalDocumentos() === 0
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'border-red-500 text-red-600 hover:bg-red-50'
            )}
          >
            {exportingPdf ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <FileText className="h-4 w-4" />
            )}
            PDF
          </button>
          <button
            onClick={exportarExcel}
            disabled={exporting || !data || getTotalDocumentos() === 0}
            className={clsx(
              'flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors',
              exporting || !data || getTotalDocumentos() === 0
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'border-green-500 text-green-600 hover:bg-green-50'
            )}
          >
            {exporting ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <FileSpreadsheet className="h-4 w-4" />
            )}
            Excel
          </button>
        </div>
      </div>

      {/* Filtros */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-sm border border-gray-100 p-4"
      >
        <div className="flex flex-wrap items-end gap-4">
          {/* Mes */}
          <div className="flex-1 min-w-[150px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mes
            </label>
            <select
              value={mes}
              onChange={(e) => setMes(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              {MESES.map(m => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>

          {/* Año */}
          <div className="flex-1 min-w-[120px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Año
            </label>
            <select
              value={anio}
              onChange={(e) => setAnio(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              {aniosDisponibles.map(a => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
          </div>

          {/* Botón consultar */}
          <button
            onClick={cargarReporte}
            disabled={loading}
            className="flex items-center gap-2 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
          >
            {loading ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <Filter className="h-4 w-4" />
            )}
            Consultar
          </button>
        </div>
      </motion.div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-500" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-8 w-8 text-primary-600 animate-spin" />
        </div>
      )}

      {/* Contenido */}
      {!loading && data && (
        <>
          {/* Cards de Resumen */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Total Impuesto */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl shadow-sm border border-gray-100 p-5"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total {config.nombre}</p>
                  <p className={clsx(
                    'text-2xl font-bold mt-1',
                    tipoImpuesto === '07' ? 'text-red-600' : 'text-rose-600'
                  )}>
                    {formatCurrency(getTotal())}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    {MESES[mes-1].label} {anio}
                  </p>
                </div>
                <div className={clsx(
                  'p-3 rounded-lg',
                  tipoImpuesto === '07' ? 'bg-red-100' : 'bg-rose-100'
                )}>
                  <DollarSign className={clsx(
                    'h-6 w-6',
                    tipoImpuesto === '07' ? 'text-red-600' : 'text-rose-600'
                  )} />
                </div>
              </div>
            </motion.div>

            {/* Total Documentos */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl shadow-sm border border-gray-100 p-5"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Documentos</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {getTotalDocumentos()}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    facturas con impuesto
                  </p>
                </div>
                <div className="p-3 bg-blue-100 rounded-lg">
                  <FileText className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </motion.div>

            {/* Clientes */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl shadow-sm border border-gray-100 p-5"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Clientes</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {new Set(data.porCliente?.map(c => c.clienteId) || []).size}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    con este impuesto
                  </p>
                </div>
                <div className="p-3 bg-indigo-100 rounded-lg">
                  <Users className="h-6 w-6 text-indigo-600" />
                </div>
              </div>
            </motion.div>
          </div>

          {/* Tabs de Vista */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100">
            <div className="border-b border-gray-200">
              <div className="flex items-center justify-between">
                <nav className="flex -mb-px">
                  <button
                    onClick={() => setVistaActiva('resumen')}
                    className={clsx(
                      'px-6 py-3 text-sm font-medium border-b-2 transition-colors',
                      vistaActiva === 'resumen'
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    )}
                  >
                    Por Cliente
                  </button>
                  <button
                    onClick={() => setVistaActiva('detalle')}
                    className={clsx(
                      'px-6 py-3 text-sm font-medium border-b-2 transition-colors',
                      vistaActiva === 'detalle'
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    )}
                  >
                    Detalle por Factura
                  </button>
                </nav>
                {vistaActiva === 'detalle' && (
                  <div className="relative mr-3">
                    <button onClick={() => setShowColConfig(!showColConfig)} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg" title="Configurar columnas">
                      <Settings size={20} />
                    </button>
                    {showColConfig && (
                      <div className="absolute right-0 top-full mt-1 bg-white border rounded-lg shadow-lg p-2 z-20 min-w-[180px]">
                        {ALL_DETAIL_COLS.map(col => (
                          <label key={col.id} className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 rounded cursor-pointer text-xs">
                            <input type="checkbox" checked={detailCols.includes(col.id)} onChange={() => toggleDetailCol(col.id)} className="rounded border-gray-300 text-blue-600" />
                            {col.label}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Vista Por Cliente */}
            {vistaActiva === 'resumen' && (
              <div className="p-4">
                {!data.porCliente || data.porCliente.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No hay datos para el período seleccionado
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="text-left text-sm text-gray-500 border-b">
                          <th className="pb-3 font-medium">Cliente</th>
                          <th className="pb-3 font-medium">Cédula</th>
                          <th className="pb-3 font-medium text-center">Documentos</th>
                          <th className="pb-3 font-medium text-right">Total Impuesto</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {data.porCliente.map((item, idx) => (
                          <tr key={idx} className="text-sm">
                            <td className="py-3 font-medium text-gray-900">
                              {item.clienteNombre}
                            </td>
                            <td className="py-3 text-gray-600">
                              {item.clienteIdentificacion}
                            </td>
                            <td className="py-3 text-center text-gray-600">
                              {item.totalDocumentos}
                            </td>
                            <td className="py-3 text-right font-medium text-gray-900">
                              {formatCurrency(item.totalMonto)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="border-t-2 border-gray-200">
                        <tr className="font-bold">
                          <td className="py-3 text-gray-900">
                            TOTAL: {data.porCliente.length} {data.porCliente.length === 1 ? 'cliente' : 'clientes'}
                          </td>
                          <td className="py-3"></td>
                          <td className="py-3 text-center text-gray-900">
                            {data.porCliente.reduce((sum, c) => sum + (Number(c.totalDocumentos) || 0), 0)}
                          </td>
                          <td className={clsx(
                            'py-3 text-right',
                            tipoImpuesto === '07' ? 'text-red-600' : 'text-rose-600'
                          )}>
                            {formatCurrency(data.porCliente.reduce((sum, c) => sum + c.totalMonto, 0))}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Vista Detalle */}
            {vistaActiva === 'detalle' && (
              <div className="p-4">
                {!data.detalle || data.detalle.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No hay datos para el período seleccionado
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="text-left text-sm text-gray-500 border-b">
                          {detailCols.map(colId => {
                            const col = ALL_DETAIL_COLS.find(c => c.id === colId);
                            const isRight = ['baseCRC', 'impuestoCRC', 'totalFactCRC', 'subtotalOrig', 'tc', 'tarifa'].includes(colId);
                            return <th key={colId} className={`pb-3 font-medium ${isRight ? 'text-right' : ''}`}>{col?.label}</th>;
                          })}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {data.detalle.map((item, idx) => (
                          <tr key={idx} className="text-sm">
                            {detailCols.map(colId => {
                              switch (colId) {
                                case 'fecha': return <td key={colId} className="py-3 text-gray-600">{formatDate(item.fechaEmision)}</td>;
                                case 'consecutivo': return <td key={colId} className="py-3 font-mono text-xs text-gray-600">{item.consecutivo}</td>;
                                case 'cliente': return <td key={colId} className="py-3 text-gray-900">{item.clienteNombre}</td>;
                                case 'cedula': return <td key={colId} className="py-3 text-gray-500 text-xs">{item.clienteIdentificacion}</td>;
                                case 'producto': return <td key={colId} className="py-3 text-gray-600 max-w-[200px] truncate" title={item.productoDescripcion}>{item.productoDescripcion}</td>;
                                case 'moneda': return <td key={colId} className="py-3 text-gray-500 text-xs">{item.moneda}</td>;
                                case 'subtotalOrig': return <td key={colId} className="py-3 text-right text-gray-600">{formatCurrency(item.subtotalLinea, item.moneda)}</td>;
                                case 'tc': return <td key={colId} className="py-3 text-right text-gray-500 text-xs">₡{(item.tipoCambio || 1).toFixed(2)}</td>;
                                case 'baseCRC': return <td key={colId} className="py-3 text-right text-gray-600">{formatColones(item.baseColones || 0)}</td>;
                                case 'impuestoCRC': return <td key={colId} className="py-3 text-right font-medium text-gray-900">{formatColones(item.montoImpuestoColones)}</td>;
                                case 'totalFactCRC': {
                                  // Total de la factura COMPLETA (no de la línea) — convertido a CRC.
                                  // Sirve para validar que la suma de impuestos cuadre con el total facturado.
                                  const totalCRC = item.moneda === 'USD'
                                    ? (item.totalComprobante || 0) * (item.tipoCambio || 1)
                                    : (item.totalComprobante || 0);
                                  return <td key={colId} className="py-3 text-right text-gray-700">{formatColones(totalCRC)}</td>;
                                }
                                case 'tarifa': return <td key={colId} className="py-3 text-right text-gray-500 text-xs">{item.tarifa}%</td>;
                                default: return <td key={colId}></td>;
                              }
                            })}
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="border-t-2 border-gray-200">
                        {(() => {
                          // Total impuesto = suma de TODAS las líneas (cada línea tiene su porcentaje)
                          const totalImpuesto = data.detalle.reduce((sum, d) => sum + (d.montoImpuestoColones || 0), 0);
                          // Total base CRC = suma de la base en colones GUARDADA en cada línea
                          // (base_imponible_colones), igual que el Histórico. No se recalcula al vuelo.
                          const totalBaseCRC = data.detalle.reduce((sum, d) => {
                            const base = d.baseColones || 0;
                            return sum + base;
                          }, 0);
                          // Total facturado = suma del total_comprobante de cada documento ÚNICO
                          // (sin duplicar por línea — una FE con 3 líneas aparece 3 veces en el detalle)
                          const facturasUnicas = new Map();
                          for (const d of data.detalle) {
                            if (!facturasUnicas.has(d.documentoId)) {
                              const totalCRC = d.moneda === 'USD'
                                ? (d.totalComprobante || 0) * (d.tipoCambio || 1)
                                : (d.totalComprobante || 0);
                              facturasUnicas.set(d.documentoId, totalCRC);
                            }
                          }
                          const totalFacturado = [...facturasUnicas.values()].reduce((s, v) => s + v, 0);
                          // Tarifa esperada según el impuesto (911=0.75%, Cruz Roja=1%)
                          const tarifaEsperada = tipoImpuesto === '07' ? 0.75 : 1.0;
                          // Tarifa efectiva real (puede diferir levemente por redondeo USD→CRC línea por línea)
                          const tarifaEfectiva = totalBaseCRC > 0 ? (totalImpuesto / totalBaseCRC * 100) : 0;
                          // Diferencia entre impuesto cobrado y el teórico exacto (es esperada por redondeo legal)
                          const impuestoTeorico = totalBaseCRC * tarifaEsperada / 100;
                          const diferenciaCRC = totalImpuesto - impuestoTeorico;

                          return (
                            <>
                              <tr className="font-bold bg-gray-50">
                                {detailCols.map((colId, idx) => {
                                  if (idx === 0) return <td key={colId} className="py-3 text-gray-900">TOTAL ({facturasUnicas.size} factura{facturasUnicas.size !== 1 ? 's' : ''})</td>;
                                  if (colId === 'baseCRC') return (
                                    <td key={colId} className="py-3 text-right text-gray-900">
                                      {formatColones(totalBaseCRC)}
                                    </td>
                                  );
                                  if (colId === 'impuestoCRC') return (
                                    <td key={colId} className={clsx('py-3 text-right', tipoImpuesto === '07' ? 'text-red-600' : 'text-rose-600')}>
                                      {formatColones(totalImpuesto)}
                                    </td>
                                  );
                                  if (colId === 'totalFactCRC') return (
                                    <td key={colId} className="py-3 text-right text-gray-900"
                                        title="Suma de total_comprobante de facturas únicas">
                                      {formatColones(totalFacturado)}
                                    </td>
                                  );
                                  return <td key={colId}></td>;
                                })}
                              </tr>
                              {/* Fila de validación: muestra el cálculo teórico vs real solo como
                                  control INTERNO. El monto LEGAL a reportar es el del tfoot principal
                                  (es lo cobrado en cada FE de Hacienda — suma de impuesto_monto por línea). */}
                              {detailCols.includes('baseCRC') && detailCols.includes('impuestoCRC') && (
                                <tr className="text-xs text-gray-500 bg-gray-50/50 border-t border-gray-100">
                                  {detailCols.map((colId, idx) => {
                                    if (idx === 0) return <td key={colId} className="py-2 italic">Control interno ({tarifaEsperada}% teórico):</td>;
                                    if (colId === 'baseCRC') return <td key={colId} className="py-2 text-right">× {tarifaEsperada}%</td>;
                                    if (colId === 'impuestoCRC') return (
                                      <td key={colId} className="py-2 text-right" title="Cálculo teórico solo para validar. El monto a reportar es el del TOTAL de arriba (lo que efectivamente se cobró en Hacienda por línea, con su redondeo legal).">
                                        ≈ {formatColones(impuestoTeorico)}
                                        <div className={clsx('text-[10px] mt-0.5', Math.abs(diferenciaCRC) > totalImpuesto * 0.01 ? 'text-amber-600' : 'text-emerald-600')}>
                                          Δ {diferenciaCRC >= 0 ? '+' : ''}{formatColones(diferenciaCRC)} {Math.abs(diferenciaCRC) <= totalImpuesto * 0.01 ? '✓' : '⚠'}
                                        </div>
                                      </td>
                                    );
                                    return <td key={colId}></td>;
                                  })}
                                </tr>
                              )}
                              {/* Nota legal aclarando qué monto se reporta */}
                              {detailCols.includes('impuestoCRC') && (
                                <tr className="text-[11px] text-gray-500">
                                  <td colSpan={detailCols.length} className="py-2 px-2 italic bg-gray-50/30">
                                    💡 El monto a reportar al {config?.entidad || 'ente'} es el <strong className="text-gray-700">TOTAL real</strong> de arriba ({formatColones(totalImpuesto)}) —
                                    es la suma de cada línea según se cobró en Hacienda. El "control interno" es solo verificación;
                                    la pequeña diferencia es redondeo legal {data.detalle.some(d => d.moneda !== 'CRC') && '(cada línea USD se redondea a 2 decimales antes de convertir a CRC)'}.
                                  </td>
                                </tr>
                              )}
                            </>
                          );
                        })()}
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
